import { RouteConfig, NavigationGuard, Route } from 'vue-router';
import { TrackedPage } from '@/_core/tracking/trackedpages';

import { currentUserWsRoles, hasStorymapAccess } from '@/_core/utils/roles';
import { FeatureAddOn, subscriptionManager } from '@/_core/utils/features';
import { PublishedStorymapAccessKind } from '@/pages/main/storymap/utils/publishedStorymapRoutes';

const storymapGuard: NavigationGuard = (to, from, next) => {
    if (hasStorymapAccess()) {
        next();
    } else {
        next('/storymapsunauthorized');
    }
};

const feedbackPortalGuard: NavigationGuard = (to, from, next) => {
    if (!currentUserWsRoles.isObserver || (currentUserWsRoles.isObserver && to.name === 'ideaportal')) {
        if (initAppData.currentWs.isShowTrialPeriodIsExpired || initAppData.currentWs.isShowSubcriptionIsSuspended) {
            next('/expiredtrial/' + to.name);
        } else {
            next();
        }
    } else {
        next(`/${to.params['productSlug']}/feedbackportal/unauthorized`);
    }
};

const expiredTrialGuard: NavigationGuard = (to, from, next) => {
    if (initAppData.currentWs.isShowTrialPeriodIsExpired || initAppData.currentWs.isShowSubcriptionIsSuspended) {
        next('/expiredtrial/' + to.name);
    } else {
        next();
    }
};

const workspaceSettingsGuard: NavigationGuard = (to, from, next) => {
    if (initAppData.currentWs.isShowSubcriptionIsSuspended) {
        next('/expiredtrial/' + to.name);
    } else if (currentUserWsRoles.isWorkspaceAdmin || currentUserWsRoles.isSubscriptionAdmin) {
        next();
    } else {
        next('/');
    }
};

const env = process.env.NODE_ENV;

const routes: RouteConfig[] = [
    {
        path: '/',
        name: 'dashboard',
        meta: {
            pageVisiteEvent: TrackedPage.WorkspaceDashboard,
        },
        props: (route) => ({ justSignedUp: route.query.justSignedUp }),
        component: () => import(/* webpackChunkName: "dashboard" */ '../pages/main/dashboard/Dashboard.vue'),
    },
    {
        path: '/expiredtrial/:pageName',
        name: 'expiredtrial',
        component: () => import(/* webpackChunkName: "expiredtrial" */ '../pages/main/feedbackportal/ExpiredTrial.vue'),
        props: (route) => ({
            pageName: route.params.pageName,
        }),
        beforeEnter: (to: Route, from: Route, next) => {
            if (!initAppData.currentWs.isShowTrialPeriodIsExpired && !initAppData.currentWs.isShowSubcriptionIsSuspended) {
                next('/');
            } else {
                next();
            }
        },
    },
    {
        path: '/corporatesubscriptionmanagement',
        component: () =>
            import(
                /* webpackChunkName: "corporateSubscriptionManagement" */ '@/pages/components/corporateSubscription/corporateSubscription.vue'
            ),
        name: 'corporateSubscriptionManagement',
        beforeEnter: (to, from, next) => {
            if (initAppData.isCorporateSubscriptionManager) {
                next();
            } else {
                next('/');
            }
        },
    },
    {
        path: '/:productSlug/storymaps/:collectionSlug',
        name: 'storymapsCollection',
        meta: {
            //TODO: pageVisiteEvent:,
        },
        props: (route) => ({ collectionSlug: route.params.collectionSlug, productSlug: route.params.productSlug }),
        component: () => import(/* webpackChunkName: "storymapsCollection" */ '../pages/main/storymaps/Storymaps.vue'),
        beforeEnter: storymapGuard,
    },
    {
        path: '/:productSlug/storymaps',
        name: 'storymaps',
        meta: {
            //TODO: pageVisiteEvent:,
        },
        props: (route) => ({ productSlug: route.params.productSlug }),
        component: () => import(/* webpackChunkName: "storymaps" */ '../pages/main/storymaps/Storymaps.vue'),
        beforeEnter: (to, from, next) => {
            expiredTrialGuard(to, from, next);

            storymapGuard(to, from, next);
        },
    },
    {
        path: '/:productSlug/storymaptemplates',
        name: 'storymaptemplates',
        meta: {
            //TODO: pageVisiteEvent:,
        },
        props: (route) => ({ productSlug: route.params.productSlug }),
        component: () => import(/* webpackChunkName: "storymaptemplates" */ '../pages/main/storymapTemplates/StorymapTemplates.vue'),
        beforeEnter: (to, from, next) => {
            expiredTrialGuard(to, from, next);

            storymapGuard(to, from, next);
        },
    },
    //TODO: vagy ez helyett a menüben az egyik helyen a route a másikon egy href link...
    {
        path: '/domaincontrol',
        name: 'domaincontrol',
        meta: {
            pageVisiteEvent: TrackedPage.DomainControl,
        },
        component: () => import(/* webpackChunkName: "domaincontrol" */ '../pages/root/domaincontrol/DomainControl.vue'),
    },
    {
        path: '/domaincontrol/accounts',
        name: 'domaincontrolAccounts',
        meta: {
            pageVisiteEvent: TrackedPage.ControlledDomainAccounts,
        },
        props: (route) => ({ domain: route.query.domain }),
        component: () => import(/* webpackChunkName: "domaincontrolAccounts" */ '../pages/root/domaincontrol/accounts/Accounts.vue'),
    },
    {
        path: '/:productSlug/newstorymapquickguide/:collectionId?',
        name: 'newstorymapquickguide',
        meta: {
            pageVisiteEvent: TrackedPage.NewStorymap,
        },
        props: (route) => ({
            productSlug: route.params.productSlug,
            collectionId: route.params.collectionId,
        }),
        component: () => import(/* webpackChunkName: "newstorymapquickguide" */ '../pages/main/newStorymap/NewStorymapQuickGuide.vue'),
        beforeEnter: (to, from, next) => {
            expiredTrialGuard(to, from, next);

            storymapGuard(to, from, next);
        },
    },
    {
        path: '/:productSlug/newstorymap/:collectionId?',
        name: 'newstorymap',
        meta: {
            pageVisiteEvent: TrackedPage.NewStorymap,
        },
        props: (route) => ({ productSlug: route.params.productSlug, collectionId: route.params.collectionId || '' }),
        component: () => import(/* webpackChunkName: "newstorymap" */ '../pages/main/newStorymap/NewStorymap.vue'),
        beforeEnter: (to, from, next) => {
            expiredTrialGuard(to, from, next);

            storymapGuard(to, from, next);
        },
    },
    {
        path: '/:productSlug/newstorymapbyjira/:collectionId?',
        name: 'newstorymapbyjira',
        props: (route) => ({ productSlug: route.params.productSlug, collectionId: route.params.collectionId || '' }),
        component: () => import(/* webpackChunkName: "newstorymapbyjira" */ '../pages/main/newStorymap/StorymapByJira.vue'),
        beforeEnter: (to, from, next) => {
            expiredTrialGuard(to, from, next);

            storymapGuard(to, from, next);
        },
    },
    {
        path: '/:productSlug/newstorymapbycopy/:collectionId?',
        name: 'newstorymapbycopy',
        meta: {
            pageVisiteEvent: TrackedPage.NewStorymap,
        },
        props: (route) => ({ productSlug: route.params.productSlug, collectionId: route.params.collectionId || '' }),
        component: () => import(/* webpackChunkName: "newstorymapbycopy" */ '../pages/main/newStorymap/StorymapByCopy.vue'),
        beforeEnter: (to, from, next) => {
            expiredTrialGuard(to, from, next);

            storymapGuard(to, from, next);
        },
    },
    {
        path: '/:productSlug/newstorymapbyimport/:collectionId?',
        name: 'newstorymapbyimport',
        meta: {
            pageVisiteEvent: TrackedPage.NewStorymap,
        },
        props: (route) => ({ productSlug: route.params.productSlug, collectionId: route.params.collectionId || '' }),
        component: () => import(/* webpackChunkName: "newstorymapbyimport" */ '../pages/main/newStorymap/StorymapByImport.vue'),
        beforeEnter: (to, from, next) => {
            expiredTrialGuard(to, from, next);

            storymapGuard(to, from, next);
        },
    },
    {
        path: '/ai',
        name: 'ai',
        meta: {
            pageVisiteEvent: TrackedPage.Ai,
        },
        component: () => import(/* webpackChunkName: "ai" */ '../pages/main/ai/Ai.vue'),
    },
    {
        path: '/managepeople',
        name: 'managepeople',
        props: (route) => ({
            openInviteDialog: route.query.invite,
        }),
        meta: {
            pageVisiteEvent: TrackedPage.ManagePeople,
        },
        component: () => import(/* webpackChunkName: "managepeople" */ '../pages/main/managePeople/ManagePeople.vue'),
    },
    {
        path: '/:productSlug/managecollections/:collectionSlug?',
        name: 'managecollections',
        meta: {
            pageVisiteEvent: TrackedPage.ManageStorymapCollections,
        },
        props: (route) => ({ productSlug: route.params.productSlug, collectionSlug: route.params.collectionSlug || '' }),
        component: () => import(/* webpackChunkName: "manageCollections" */ '../pages/main/manageCollections/ManageCollections.vue'),
        beforeEnter: (to, from, next) => {
            if (!subscriptionManager.hasFeatureAddOn(FeatureAddOn.StorymapCollections)) {
                next('/');
            } else {
                next();
            }
        },
    },
    {
        path: '/:productSlug/managestorymaps',
        name: 'managestorymaps',
        meta: {
            pageVisiteEvent: TrackedPage.ManageStorymaps,
        },
        props: (route) => ({ productSlug: route.params.productSlug }),
        component: () => import(/* webpackChunkName: "managestorymaps" */ '../pages/main/manageStorymaps/ManageStorymaps.vue'),
        beforeEnter: (to, from, next) => {
            expiredTrialGuard(to, from, next);
        },
    },
    {
        path: '/:productSlug/managestorymaptemplates',
        name: 'managestorymaptemplates',
        meta: {
            pageVisiteEvent: TrackedPage.ManageStorymapTemplates,
        },
        props: (route) => ({ productSlug: route.params.productSlug }),
        component: () =>
            import(/* webpackChunkName: "managestorymaptemplates" */ '../pages/main/manageStorymapTemplates/ManageStorymapTemplates.vue'),
    },
    {
        path: '/:productSlug/managepersonas/:personaId?',
        name: 'managepersonas',
        meta: {
            pageVisiteEvent: TrackedPage.ManagePersonas,
        },
        props: (route) => ({
            personaId: route.params.personaId,
            productSlug: route.params.productSlug,
        }),
        component: () => import(/* webpackChunkName: "managepersonas" */ '../pages/main/managePersonas/ManagePersonas.vue'),
        beforeEnter: (to, from, next) => {
            expiredTrialGuard(to, from, next);
        },
    },
    {
        path: '/editstorymap/:storymapSlug',
        name: 'editstorymap',
        meta: {
            pageVisiteEvent: TrackedPage.ManageStorymapsEditStorymap,
        },
        props: (route) => ({
            storymapSlug: route.params.storymapSlug,
            collaborators: route.query.collaborators,
            sharing: route.query.sharing,
        }),
        component: () => import(/* webpackChunkName: "editstorymap" */ '../pages/main/editStorymap/EditStorymap.vue'),
    },
    {
        path: '/storymap/shared/:token/release/:releaseId?',
        name: 'publishedStorymapPrivateRelease',
        meta: {
            isPublishedRecipient: true,
        },
        props: (route) => ({
            isPublishedRecipient: true,
            accessKind: PublishedStorymapAccessKind.PrivateLink,
            accessKey: route.params.token,
            releaseId: route.params.releaseId,
            viewType: route.query.viewType,
        }),
        component: () => import(/* webpackChunkName: "storymap" */ '../pages/main/storymap/Storymap.vue'),
    },
    {
        path: '/storymap/shared/:token/:cardId?',
        name: 'publishedStorymapPrivate',
        meta: {
            isPublishedRecipient: true,
        },
        props: (route) => ({
            isPublishedRecipient: true,
            accessKind: PublishedStorymapAccessKind.PrivateLink,
            accessKey: route.params.token,
            cardId: route.params.cardId,
            viewType: route.query.viewType,
        }),
        component: () => import(/* webpackChunkName: "storymap" */ '../pages/main/storymap/Storymap.vue'),
    },
    {
        path: '/storymap/:storymapSlug/release/:releaseId?',
        name: 'storymapRelease',
        meta: {
            //pageVisiteEvent: TrackedPage.Storymap,
        },
        props: (route) => ({
            storymapSlug: route.params.storymapSlug,
            releaseId: route.params.releaseId,
            viewType: route.query.viewType,
        }),
        component: () => import(/* webpackChunkName: "storymap" */ '../pages/main/storymap/Storymap.vue'),
    },
    {
        path: '/storymap/:storymapSlug/:cardId?',
        alias: ['/storymap/:storymapSlug/!card/:cardId?', '/storymapcard/:storymapSlug?/:cardId?'],
        name: 'storymap',
        meta: {
            //pageVisiteEvent: TrackedPage.Storymap,
        },
        props: (route) => ({
            storymapSlug: route.params.storymapSlug,
            cardId: route.params.cardId,
            trackingReleaseId: route.query.trackingReleaseId,
            viewType: route.query.viewType,
            justSignedUp: route.query.justSignedUp,
            aiProductDescription: route.params.aiProductDescription,
        }),
        component: () => import(/* webpackChunkName: "storymap" */ '../pages/main/storymap/Storymap.vue'),
    },
    {
        path: '/managesubscription',
        alias: '/upgrade',
        name: 'managesubscription',
        component: () => import(/* webpackChunkName: "managesubscription" */ '../pages/main/manageSubscription/ManageSubscription.vue'),
    },
    {
        path: '/billing',
        name: 'billing',
        meta: {
            pageVisiteEvent: TrackedPage.WorkspacePlansAndBilling,
        },
        component: () => import(/* webpackChunkName: "billing" */ '../pages/main/billing/Billing.vue'),
    },
    {
        path: '/checkout',
        name: 'checkout',
        component: () => import(/* webpackChunkName: "checkout" */ '../pages/main/checkout/Checkout.vue'),
    },
    {
        path: '/workspacesettings',
        name: 'workspacesettings',

        component: () => import(/* webpackChunkName: "workspacesettings" */ '../pages/main/workspacesettings/WorkspaceSettings.vue'),
        props: (route) => ({
            p: route.query.p,
        }),
        beforeEnter: workspaceSettingsGuard,
    },
    {
        path: '/workspacesettings/accesshistory',
        name: 'workspacesettingsAccessHistory',
        meta: {
            pageVisiteEvent: TrackedPage.WorkspaceAccessHistory,
        },
        component: () =>
            import(
                /* webpackChunkName: "workspacesettingsWorkspaceAccessHistory" */ '../pages/main/workspacesettings/components/advancedSecurity/workspaceAccessHistory/WorkspaceAccessHistory.vue'
            ),
    },
    {
        path: '/workspacesettings/auditlog/:userId?/:fromDateTimeStamp?',
        name: 'workspacesettingsAuditLog',
        meta: {
            pageVisiteEvent: TrackedPage.WorkspaceAuditLog,
        },
        props: (route) => ({
            userId: route.params.userId,
            fromDateTimeStamp: route.params.fromDateTimeStamp,
        }),
        component: () =>
            import(
                /* webpackChunkName: "workspacesettingsAuditLog" */ '../pages/main/workspacesettings/components/advancedSecurity/auditLog/AuditLog.vue'
            ),
        children: [
            {
                path: '!/boardactivities/:auditLogEntryId',
                name: 'workspacesettingsAuditLogBoardActivitiesDialog',
                component: () =>
                    import(
                        /* webpackChunkName: "workspacesettingsAuditLogBoardActivitiesDialog" */ '../pages/main/workspacesettings/components/advancedSecurity/auditLog/BoardActivitiesDialog.vue'
                    ),
                props: (route) => ({
                    auditLogEntryId: route.params.auditLogEntryId,
                }),
            },
        ],
    },
    {
        path: '/integrationsettings',
        name: 'integrationsettings',
        meta: {
            pageVisiteEvent: TrackedPage.ManageJiraIntegrationSettings,
        },
        component: () => import(/* webpackChunkName: "wsjirasettings" */ '../pages/main/wsJiraSettings/WsJiraSettings.vue'),
    },
    {
        path: '/newworkspace',
        name: 'newworkspace',
        meta: {
            pageVisiteEvent: TrackedPage.NewWorkspace,
        },
        component: () => import(/* webpackChunkName: "newworkspace" */ '../pages/root/newWorkspace/NewWorkspace.vue'),
    },
    {
        path: '/:productSlug/feedbackportal/insights/:feedbackId?',
        name: 'insights',
        meta: {
            pageVisiteEvent: TrackedPage.FeedbackPortalInsights,
        },
        component: () => import(/* webpackChunkName: "feedbackportal" */ '../pages/main/feedbackportal/Insights.vue'),
        props: (route) => ({
            isCreatingFeedback: route.query.create,
            feedbackId: route.params.feedbackId,
            connectTab: route.query.connectTab,
            productSlug: route.params.productSlug,
        }),
        beforeEnter: feedbackPortalGuard,
    },
    {
        path: '/:productSlug/feedbackportal/ideas/:ideaId?/insights/:feedbackId?',
        alias: '/:productSlug/feedbackportal/ideas/:ideaId?',
        name: 'ideas',
        meta: {
            pageVisiteEvent: TrackedPage.FeedbackPortalIdeas,
        },
        component: () => import(/* webpackChunkName: "feedbackportalideas" */ '../pages/main/feedbackportal/Ideas.vue'),
        props: (route) => ({
            isCreatingIdea: route.query.create === undefined ? false : true,
            ideaId: route.params.ideaId,
            feedbackId: route.params.feedbackId,
            productSlug: route.params.productSlug,
        }),
        beforeEnter: feedbackPortalGuard,
    },
    {
        path: '/:productSlug/feedbackportal/p/:slug?',
        name: 'ideaportal',
        meta: {
            // pageVisiteEvent: TrackedPage.FeedbackPortalIdeaPortal, TODO: ez legyen?
        },
        component: () => import(/* webpackChunkName: "portal" */ '../pages/main/feedbackportal/IdeaPortal.vue'),
        props: (route) => ({
            slug: route.params.slug,
            newPortal: route.query.newPortal,
            loadDefaultPortalIfNotFound: route.query.loadDefaultPortalIfNotFound,
            productSlug: route.params.productSlug,
        }),
        beforeEnter: feedbackPortalGuard,
    },
    {
        path: '/:productSlug/feedbackportal/unauthorized',
        name: 'feedbackportalUnauthorized',
        component: () =>
            import(/* webpackChunkName: "feedbackportalUnauthorized" */ '../pages/main/feedbackportal/FeedbackportalUnauthorized.vue'),
        props: (route) => ({
            productSlug: route.params.productSlug,
        }),
    },
    {
        path: '/storymapsunauthorized',
        name: 'storymapsunauthorized',
        component: () =>
            import(/* webpackChunkName: "storymapsunauthorized" */ '../pages/main/components/storymaps/StorymapsUnauthorized.vue'),
    },
    {
        path: '/r/:slug/:releaseId',
        name: 'releaseview',
        props: (route) => ({
            slug: route.params.slug,
            releaseId: route.params.releaseId,
        }),
        beforeEnter: (to, from, next) => {
            storymapGuard(to, from, next);

            next(`/storymap/${to.params['slug']}?viewType=tracking&trackingReleaseId=${to.params['releaseId']}`);
        },
    },
    {
        path: '/:productSlug/roadmaps',
        name: 'roadmaps',
        meta: {
            //TODO: pageVisiteEvent:,
        },
        props: (route) => ({ productSlug: route.params.productSlug }),
        component: () => import(/* webpackChunkName: "roadmaps" */ '../pages/main/roadmaps/Roadmaps.vue'),
        beforeEnter: expiredTrialGuard,
    },
    {
        path: '/roadmap/:id/:cardId?',
        name: 'roadmap',
        props: (route) => ({
            id: route.params.id,
            cardId: route.params.cardId,
            justCreated: route.query.justCreated,
            viewType: route.query.viewType,
        }),
        meta: {
            //TODO: pageVisiteEvent:,
        },
        component: () => import(/* webpackChunkName: "roadmap" */ '../pages/main/roadmap/Roadmap.vue'),
        beforeEnter: expiredTrialGuard,
    },
    {
        path: '/:productSlug/newroadmap',
        name: 'newroadmap',
        meta: {
            //TODO: pageVisiteEvent:,
        },
        props: (route) => ({ productSlug: route.params.productSlug }),
        beforeEnter: (to, from, next) => {
            expiredTrialGuard(to, from, next);

            if (currentUserWsRoles.isObserver) {
                next('/');
            } else {
                next();
            }
        },
        component: () => import(/* webpackChunkName: "newroadmap" */ '../pages/main/roadmaps/RoadmapCreate.vue'),
    },
    {
        path: '/:productSlug/newroadmapfromstorymap/:storymapId',
        name: 'newroadmapfromstorymap',
        meta: {
            //TODO: pageVisiteEvent:,
        },
        beforeEnter: (to, from, next) => {
            if (currentUserWsRoles.isObserver) {
                next('/');
            } else {
                next();
            }
        },
        props: (route) => ({
            storymapId: route.params.storymapId,
            productSlug: route.params.productSlug,
        }),
        component: () => import(/* webpackChunkName: "newroadmapfromstorymap" */ '../pages/main/roadmaps/RoadmapCreateFromStorymap.vue'),
    },
    {
        path: '/products',
        name: 'products',
        meta: {
            pageVisiteEvent: TrackedPage.ManageProducts,
        },
        component: () => import(/* webpackChunkName: "manageproducts" */ '../pages/main/manageProducts/ManageProducts.vue'),
    },
    {
        path: '/:productSlug/dashboard/:collectionSlug?',
        alias: '/:productSlug',
        name: 'product_dashboard',
        props: (route) => ({ productSlug: route.params.productSlug, collectionSlug: route.params.collectionSlug || '' }),
        meta: {
            pageVisiteEvent: TrackedPage.ProductDashboard,
        },
        component: () => import(/* webpackChunkName: "productDashboard" */ '../pages/main/product/ProductDashboard.vue'),
        beforeEnter: (to, from, next) => {
            expiredTrialGuard(to, from, next);
        },
    },
];

if (env === 'development') {
    routes.push({
        path: '/devicons',
        name: 'devicons',
        component: () => import(/* webpackChunkName: "devicons" */ '../pages/dev/DevIcons.vue'),
    });
}

export { routes };
